import {
  ARTIST_PAGE_SIZE,
  type Artist,
  type ListArtistsParams,
  type PaginatedArtists,
  getArtist,
  queryArtists,
} from './artists';
import { type DashboardData, getDashboard } from './dashboard';
import {
  type ListVoterEmailsParams,
  type PaginatedVoterEmails,
  VOTER_EMAIL_PAGE_SIZE,
  queryVoterEmails,
} from './emails';
import { useDebouncedValue } from './hooks';
import { type Me, getMe } from './me';
import { type ListMediaParams, MEDIA_PAGE_SIZE, type PaginatedMedia, queryMedia } from './media';
import {
  type ListNotificationsParams,
  NOTIFICATION_PAGE_SIZE,
  type PaginatedNotifications,
  queryNotifications,
} from './notifications';
import {
  PROJECT_PAGE_SIZE,
  type PaginatedProjects,
  type Project,
  type ProjectQuery,
  getProject,
  queryProjects,
} from './projects';
import {
  type ListProposalsParams,
  PROPOSAL_PAGE_SIZE,
  type PaginatedProposals,
  type Proposal,
  getProposal,
  queryProposals,
} from './proposals';
import { useResource } from './resource';
import { type Settings, getSettings } from './settings';
import {
  type ListSubscribersParams,
  type PaginatedSubscribers,
  SUBSCRIBER_PAGE_SIZE,
  querySubscribers,
} from './subscribers';
import {
  type ListMembersParams,
  MEMBER_PAGE_SIZE,
  type Member,
  type PaginatedMembers,
  getMember,
  listMembers,
} from './users';
import { type ListVotesParams, type PaginatedVotes, VOTE_PAGE_SIZE, queryVotes } from './votes';
import {
  type ListVotingPeriodsParams,
  type PaginatedVotingPeriods,
  VOTING_PAGE_SIZE,
  type VotingResults,
  getVotingResults,
  queryVotingPeriods,
} from './voting';

/**
 * Per-resource data hooks. Each one is a thin wrapper around `useResource`, so
 * they all get caching, request de-duplication and background revalidation.
 */

export function useProjects(query: ProjectQuery) {
  const search = useDebouncedValue(query.search);
  const key = [
    'projects',
    query.status ?? 'All',
    query.order ?? 'newest',
    query.page ?? 1,
    query.pageSize ?? PROJECT_PAGE_SIZE,
    search?.trim() ?? '',
  ].join(':');

  return useResource<PaginatedProjects>(key, () => queryProjects({ ...query, search }));
}

export function useProject(slug: string | undefined) {
  return useResource<Project | undefined>(
    `project:${slug ?? ''}`,
    () => (slug ? getProject(slug) : Promise.resolve(undefined)),
    { enabled: Boolean(slug) },
  );
}

export function useArtists(query: ListArtistsParams) {
  const search = useDebouncedValue(query.search);
  const key = [
    'artists',
    query.status ?? 'all',
    query.order ?? 'newest',
    query.page ?? 1,
    query.pageSize ?? ARTIST_PAGE_SIZE,
    search?.trim() ?? '',
  ].join(':');

  return useResource<PaginatedArtists>(key, () => queryArtists({ ...query, search }));
}

export function useArtist(slug: string | undefined) {
  return useResource<Artist | undefined>(
    `artist:${slug ?? ''}`,
    () => (slug ? getArtist(slug) : Promise.resolve(undefined)),
    { enabled: Boolean(slug) },
  );
}

export function useProposals(query: ListProposalsParams) {
  const search = useDebouncedValue(query.search);
  const key = [
    'proposals',
    query.status ?? 'All',
    query.project ?? '',
    query.artistSlug ?? '',
    query.order ?? 'newest',
    query.page ?? 1,
    query.pageSize ?? PROPOSAL_PAGE_SIZE,
    search?.trim() ?? '',
  ].join(':');

  return useResource<PaginatedProposals>(key, () => queryProposals({ ...query, search }));
}

export function useProposal(slug: string | undefined) {
  return useResource<Proposal | undefined>(
    `proposal:${slug ?? ''}`,
    () => (slug ? getProposal(slug) : Promise.resolve(undefined)),
    { enabled: Boolean(slug) },
  );
}

export function useMedia(query: ListMediaParams) {
  const search = useDebouncedValue(query.search);
  const key = [
    'media',
    query.type ?? 'all',
    query.order ?? 'newest',
    query.page ?? 1,
    query.pageSize ?? MEDIA_PAGE_SIZE,
    search?.trim() ?? '',
  ].join(':');

  return useResource<PaginatedMedia>(key, () => queryMedia({ ...query, search }));
}

export function useVotingPeriods(query: ListVotingPeriodsParams) {
  const search = useDebouncedValue(query.search);
  const key = [
    'voting:periods',
    query.status ?? 'all',
    query.order ?? 'newest',
    query.page ?? 1,
    query.pageSize ?? VOTING_PAGE_SIZE,
    search?.trim() ?? '',
  ].join(':');

  return useResource<PaginatedVotingPeriods>(key, () => queryVotingPeriods({ ...query, search }));
}

export function useVotingResults(projectSlug: string | undefined) {
  return useResource<VotingResults | undefined>(
    `voting:results:${projectSlug ?? ''}`,
    () => (projectSlug ? getVotingResults(projectSlug) : Promise.resolve(undefined)),
    { enabled: Boolean(projectSlug) },
  );
}

export function useVotes(query: ListVotesParams) {
  const search = useDebouncedValue(query.search);
  const key = [
    'votes',
    query.project ?? '',
    query.page ?? 1,
    query.pageSize ?? VOTE_PAGE_SIZE,
    search?.trim() ?? '',
  ].join(':');

  return useResource<PaginatedVotes>(key, () => queryVotes({ ...query, search }));
}

export function useSubscribers(query: ListSubscribersParams) {
  const search = useDebouncedValue(query.search);
  const key = [
    'subscribers',
    query.source ?? 'all',
    query.page ?? 1,
    query.pageSize ?? SUBSCRIBER_PAGE_SIZE,
    search?.trim() ?? '',
  ].join(':');

  return useResource<PaginatedSubscribers>(key, () => querySubscribers({ ...query, search }));
}

export function useVoterEmails(query: ListVoterEmailsParams) {
  const search = useDebouncedValue(query.search);
  const key = [
    'emails',
    query.page ?? 1,
    query.pageSize ?? VOTER_EMAIL_PAGE_SIZE,
    search?.trim() ?? '',
  ].join(':');

  return useResource<PaginatedVoterEmails>(key, () => queryVoterEmails({ ...query, search }));
}

export function useDashboard() {
  return useResource<DashboardData>('dashboard', getDashboard);
}

export function useNotifications(query: ListNotificationsParams) {
  const key = [
    'notifications',
    query.read ?? 'all',
    query.actorId ?? '',
    query.page ?? 1,
    query.pageSize ?? NOTIFICATION_PAGE_SIZE,
  ].join(':');

  return useResource<PaginatedNotifications>(key, () => queryNotifications(query));
}

export function useMe() {
  return useResource<Me>('me', getMe);
}

export function useSettings() {
  return useResource<Settings>('settings', getSettings);
}

export function useUsers(query: ListMembersParams) {
  const search = useDebouncedValue(query.search);
  const key = [
    'users',
    query.role ?? 'all',
    search?.trim() ?? '',
    query.page ?? 1,
    query.pageSize ?? MEMBER_PAGE_SIZE,
  ].join(':');

  return useResource<PaginatedMembers>(key, () => listMembers({ ...query, search }));
}

export function useMember(id: string | undefined) {
  return useResource<Member | undefined>(
    `member:${id ?? ''}`,
    () => (id ? getMember(id) : Promise.resolve(undefined)),
    { enabled: Boolean(id) },
  );
}

export function useMemberActivity(id: string | undefined) {
  return useResource<PaginatedNotifications>(
    `notifications:member:${id ?? ''}`,
    () => queryNotifications({ actorId: id, pageSize: 50 }),
    { enabled: Boolean(id) },
  );
}
